/**
 * Block 12 PR-B Phase B3 Part C Step 10 — useAnketaForm hook.
 *
 * Container-level hook оборачивающий `useReducer(anketaReducer, ...)` с lazy
 * initialization из `createInitialState` или `initialDraft` через HYDRATE_FROM_DRAFT
 * action (ensures validation recomputes against current refined schema — defensive
 * против IDB shape drift между app versions, ADR-0020 §Q7).
 *
 * Actions — useMemo-curried dispatchers (stable identity across renders).
 *
 * Reference: ADR-0020 §Q2 (useReducer local per Container, no global Zustand),
 * §Q7 (validation recompute), §Q10 (URL guard handled в Container, не здесь).
 */

import { useMemo, useReducer } from 'react';
import type { CalculatorAnketaType } from '@pomoshnik/shared';
import { anketaReducer, createInitialState } from './state/anketaReducer';
import type { AnketaState, FieldError, MaterialRow } from './types';

/**
 * Curried action surface — каждый метод dispatch-ит соответствующий AnketaAction.
 * Stable identity per render (useMemo с empty deps — dispatch стабилен из useReducer).
 *
 * Naming: глагол + cекция / поле. SET_ → setX, OVERRIDE_*_QTY → overrideXQty,
 * RESET_*_AUTO → resetXAuto, TOGGLE_X → toggleX.
 */
export interface AnketaFormActions {
  setParam(field: string, value: unknown): void;
  setProjectName(name: string): void;

  setMaterialPrice(rowId: string, price: number): void;
  overrideMaterialQty(rowId: string, qty: number): void;
  resetMaterialAuto(rowId: string): void;
  setBrand(rowId: string, brand: string): void;

  toggleAdditional(id: string, on: boolean): void;
  setAdditionalQty(id: string, qty: number): void;
  setAdditionalPrice(id: string, price: number): void;
  setAdditionalSelection(id: string, value: string): void;
  // Hotfix-2B Phase B4 Step 9 — Витрина compound dimensions.
  setAdditionalLength(id: string, length: number): void;
  setAdditionalHeight(id: string, height: number): void;
  setAdditionalPricePerM2(id: string, pricePerM2: number): void;

  toggleFilling(id: string, on: boolean): void;
  setFillingQty(id: string, qty: number): void;
  setFillingPrice(id: string, price: number): void;

  toggleLighting(on: boolean): void;
  setLightingQty(rowId: string, qty: number): void;
  setLightingPrice(rowId: string, price: number): void;

  toggleConsumable(id: string, on: boolean): void;
  setConsumableQty(id: string, qty: number): void;
  setConsumablePrice(id: string, price: number): void;

  toggleService(id: string, on: boolean): void;
  overrideServiceQty(id: string, qty: number): void;
  resetServiceAuto(id: string): void;
  setServicePrice(id: string, price: number): void;

  addCustomRow(row: MaterialRow): void;
  updateCustomRow(rowId: string, patch: Partial<MaterialRow>): void;
  removeCustomRow(rowId: string): void;

  setValidation(isValid: boolean, errors: FieldError[]): void;
  hydrateFromDraft(state: AnketaState): void;
  reset(anketaType: CalculatorAnketaType, projectId: string): void;
}

export interface UseAnketaFormResult {
  state: AnketaState;
  actions: AnketaFormActions;
  /**
   * Returns current `state.validation.isValid`. Convenience over selector import.
   *
   * NB: `validate` reference recreated each render. Всегда вызывай как
   * `result.current.validate()` — не сохраняй ref в long-lived useEffect/useCallback
   * с stale deps, иначе получишь outdated isValid.
   */
  validate: () => boolean;
}

/**
 * Container hook.
 *
 * @param anketaType — discriminator (URL `:type` param). Stable per Container instance.
 * @param projectId — client-generated UUID (ADR-0019 §Q11.3) или existing draft id.
 * @param initialDraft — optional, для resume сценария (`?draftId=` query). Goes through
 *   HYDRATE_FROM_DRAFT action чтобы validation recomputed против current refined schema.
 *
 * **Precedence:** если `initialDraft` provided, его `anketaType`/`projectId`/`createdAt`
 * выигрывают над hook params. Hook НЕ enforce'ит совпадение — mismatch detection
 * (URL `:type=kitchen` vs draft `type=cabinet`) — обязанность Container (Step 25
 * URL guard per ADR-0020 §Q10).
 */
export function useAnketaForm(
  anketaType: CalculatorAnketaType,
  projectId: string,
  initialDraft?: AnketaState,
): UseAnketaFormResult {
  const [state, dispatch] = useReducer(anketaReducer, undefined, () => {
    if (initialDraft) {
      return anketaReducer(
        createInitialState(initialDraft.anketaType, initialDraft.projectId, initialDraft.createdAt),
        { type: 'HYDRATE_FROM_DRAFT', state: initialDraft },
      );
    }
    return createInitialState(anketaType, projectId);
  });

  const actions = useMemo<AnketaFormActions>(
    () => ({
      setParam: (field, value) => dispatch({ type: 'SET_PARAM', field, value }),
      setProjectName: (name) => dispatch({ type: 'SET_PROJECT_NAME', name }),

      setMaterialPrice: (rowId, price) => dispatch({ type: 'SET_MATERIAL_PRICE', rowId, price }),
      overrideMaterialQty: (rowId, qty) => dispatch({ type: 'OVERRIDE_MATERIAL_QTY', rowId, qty }),
      resetMaterialAuto: (rowId) => dispatch({ type: 'RESET_MATERIAL_AUTO', rowId }),
      setBrand: (rowId, brand) => dispatch({ type: 'SET_BRAND', rowId, brand }),

      toggleAdditional: (id, on) => dispatch({ type: 'TOGGLE_ADDITIONAL', id, on }),
      setAdditionalQty: (id, qty) => dispatch({ type: 'SET_ADDITIONAL_QTY', id, qty }),
      setAdditionalPrice: (id, price) => dispatch({ type: 'SET_ADDITIONAL_PRICE', id, price }),
      setAdditionalSelection: (id, value) =>
        dispatch({ type: 'SET_ADDITIONAL_SELECTION', id, value }),
      setAdditionalLength: (id, length) => dispatch({ type: 'SET_ADDITIONAL_LENGTH', id, length }),
      setAdditionalHeight: (id, height) => dispatch({ type: 'SET_ADDITIONAL_HEIGHT', id, height }),
      setAdditionalPricePerM2: (id, pricePerM2) =>
        dispatch({ type: 'SET_ADDITIONAL_PRICE_PER_M2', id, pricePerM2 }),

      toggleFilling: (id, on) => dispatch({ type: 'TOGGLE_FILLING', id, on }),
      setFillingQty: (id, qty) => dispatch({ type: 'SET_FILLING_QTY', id, qty }),
      setFillingPrice: (id, price) => dispatch({ type: 'SET_FILLING_PRICE', id, price }),

      toggleLighting: (on) => dispatch({ type: 'TOGGLE_LIGHTING', on }),
      setLightingQty: (rowId, qty) => dispatch({ type: 'SET_LIGHTING_QTY', rowId, qty }),
      setLightingPrice: (rowId, price) => dispatch({ type: 'SET_LIGHTING_PRICE', rowId, price }),

      toggleConsumable: (id, on) => dispatch({ type: 'TOGGLE_CONSUMABLE', id, on }),
      setConsumableQty: (id, qty) => dispatch({ type: 'SET_CONSUMABLE_QTY', id, qty }),
      setConsumablePrice: (id, price) => dispatch({ type: 'SET_CONSUMABLE_PRICE', id, price }),

      toggleService: (id, on) => dispatch({ type: 'TOGGLE_SERVICE', id, on }),
      overrideServiceQty: (id, qty) => dispatch({ type: 'OVERRIDE_SERVICE_QTY', id, qty }),
      resetServiceAuto: (id) => dispatch({ type: 'RESET_SERVICE_AUTO', id }),
      setServicePrice: (id, price) => dispatch({ type: 'SET_SERVICE_PRICE', id, price }),

      addCustomRow: (row) => dispatch({ type: 'ADD_CUSTOM_ROW', row }),
      updateCustomRow: (rowId, patch) => dispatch({ type: 'UPDATE_CUSTOM_ROW', rowId, patch }),
      removeCustomRow: (rowId) => dispatch({ type: 'REMOVE_CUSTOM_ROW', rowId }),

      setValidation: (isValid, errors) => dispatch({ type: 'SET_VALIDATION', isValid, errors }),
      hydrateFromDraft: (s) => dispatch({ type: 'HYDRATE_FROM_DRAFT', state: s }),
      reset: (newType, newId) => dispatch({ type: 'RESET', anketaType: newType, projectId: newId }),
    }),
    // `dispatch` stable per React useReducer contract — empty deps корректно.
    [],
  );

  const validate = () => state.validation.isValid;

  return { state, actions, validate };
}
